import type {
  IWidgetThemeConfig,
  IThemeCacheEntry,
  IThemeApiResponse,
} from '../types';
import { collectStyleData } from '../utils/styleCollection';
import { loadGoogleFont } from '../utils/fontLoader';

const THEME_CACHE_KEY = 'widget-theme';
const COLLECTION_DELAY_MS = 3000;

/**
 * Get cached theme from localStorage.
 */
export function getCachedTheme(): IThemeCacheEntry | null {
  try {
    const cached = localStorage.getItem(THEME_CACHE_KEY);
    if (!cached) return null;

    const parsed = JSON.parse(cached) as IThemeCacheEntry;
    if (parsed && parsed.config && typeof parsed.version === 'number') {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Save theme to localStorage cache.
 */
export function setCachedTheme(config: IWidgetThemeConfig, version: number): void {
  try {
    const entry: IThemeCacheEntry = { config, version };
    localStorage.setItem(THEME_CACHE_KEY, JSON.stringify(entry));
  } catch {
    // localStorage may be blocked or full - silent failure
  }
}

/**
 * Apply theme configuration to a root element by setting CSS variables.
 */
export function applyTheme(
  root: HTMLElement,
  config: IWidgetThemeConfig
): void {
  try {
    for (const [key, value] of Object.entries(config)) {
      if (value) {
        root.style.setProperty(`--widget-${key}`, value);

        // Load Google Font if font-family changed
        if (key === 'font-family') {
          loadGoogleFont(value);
        }
      }
    }
  } catch {
    // Silent failure - widget will use defaults
  }
}

export interface IStyleCollectionCallbacks {
  onStart?: () => void;
  onSuccess?: (config: IWidgetThemeConfig, version: number) => void;
  onComplete?: (success: boolean) => void;
}

/**
 * Schedule style collection after a delay.
 * Uses requestIdleCallback if available for better performance.
 */
export function scheduleStyleCollection(
  submitFn: (payload: ReturnType<typeof collectStyleData>) => Promise<IThemeApiResponse>,
  onSuccess: (config: IWidgetThemeConfig, version: number) => void,
  callbacks?: IStyleCollectionCallbacks
): void {
  const collect = async () => {
    callbacks?.onStart?.();

    try {
      const styleData = collectStyleData();
      const response = await submitFn(styleData);

      if (response.status === 'active') {
        onSuccess(response.config, response.version);
        callbacks?.onSuccess?.(response.config, response.version);
        callbacks?.onComplete?.(true);
      } else {
        callbacks?.onComplete?.(false);
      }
    } catch {
      callbacks?.onComplete?.(false);
    }
  };

  // Schedule collection after delay
  const scheduleWithDelay = () => {
    setTimeout(collect, COLLECTION_DELAY_MS);
  };

  // Use requestIdleCallback if available for better performance
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(scheduleWithDelay, { timeout: 5000 });
  } else {
    scheduleWithDelay();
  }
}

/**
 * Initialize theme for the widget.
 * Returns cached theme immediately if available, then checks for updates.
 */
export interface IThemeInitResult {
  cachedConfig: IWidgetThemeConfig | null;
  cachedVersion: number | null;
}

export function initTheme(): IThemeInitResult {
  const cached = getCachedTheme();

  return {
    cachedConfig: cached?.config || null,
    cachedVersion: cached?.version || null,
  };
}
